import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Pencil, Sliders, Package, History, Wrench, Trash2, GitMerge, MapPin, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { PART_STATUS, VALUE_TYPE_BADGE, TRANSACTION_LABELS, ADJUSTMENT_REASONS, formatCurrency, formatDateTime } from "./inventoryConstants";
import { PART_SOURCES } from "./procurementConstants";
import { ConfirmDialog } from "./ConfirmDialog";
import { TransferStockDialog, MergePartDialog } from "./PartLocationsMerge";
import { useT } from "@/i18n/LanguageContext";

const Row = ({ label, value }) => (
  value || value === 0 ? (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-50 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right font-medium">{value}</span>
    </div>
  ) : null
);

export const PartDetailModal = ({ part, onClose, onEdit, onChanged, canManage, currentUser, vehicles = [], locations = [] }) => {
  const [txns, setTxns] = useState([]);
  const [tab, setTab] = useState("details");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [newQty, setNewQty] = useState("");
  const [reason, setReason] = useState("Count Correction");
  const [notes, setNotes] = useState("");
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueQty, setIssueQty] = useState(1);
  const [issueUnit, setIssueUnit] = useState("");
  const [issueNotes, setIssueNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const { t } = useT();
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const loadTxns = useCallback(() => {
    if (part?.id) inventoryApi.getTransactions({ part_id: part.id }).then(setTxns).catch(() => {});
  }, [part?.id]);

  useEffect(() => {
    if (part) { setTab("details"); setNewQty(String(part.quantity_on_hand)); loadTxns(); }
  }, [part, loadTxns]);

  if (!part) return null;
  const s = PART_STATUS[part.status] || PART_STATUS.in_stock;
  const vt = VALUE_TYPE_BADGE[part.value_type] || VALUE_TYPE_BADGE.estimated;

  const submitAdjust = async () => {
    try {
      await inventoryApi.adjustPart(part.id, { new_quantity: Number(newQty), reason, notes, user: userName });
      toast.success("Quantity adjusted");
      setAdjustOpen(false); setNotes("");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to adjust");
    }
  };

  const submitIssue = async () => {
    if (!issueUnit) { toast.error("Select a unit"); return; }
    const unit = vehicles.find((v) => v.id === issueUnit);
    try {
      await inventoryApi.useParts({
        part_id: part.id, unit_id: issueUnit, unit_number: unit?.unit_number, unit_type: unit?.vehicle_type,
        quantity: Number(issueQty) || 1, source: "inventory_stock", installed_by: userName, notes: issueNotes,
      });
      toast.success("Issued to unit");
      setIssueOpen(false); setIssueNotes(""); setIssueQty(1); setIssueUnit("");
      onChanged(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to issue");
    }
  };

  const compat = [part.compat_tractor && "Tractor", part.compat_chassis && "Chassis", part.compat_yard_equipment && "Yard Equipment"].filter(Boolean).join(", ");

  const handleDelete = async () => {
    try {
      await inventoryApi.deletePart(part.id);
      toast.success(t("Deleted"));
      setConfirmDelete(false);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Dialog open={!!part} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="part-detail-modal">
        <DialogTitle className="sr-only">{part.name} details</DialogTitle>
        <DialogDescription className="sr-only">Part details, ledger and actions</DialogDescription>
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            {part.photo ? (
              <img src={part.photo} alt={part.name} className="w-12 h-12 rounded-lg object-cover border" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center"><Package className="w-6 h-6 text-slate-400" /></div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{part.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs font-mono text-slate-500">{part.part_number}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.color}`}>{s.label}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex border-b border-slate-200 flex-shrink-0">
          <button onClick={() => setTab("details")} className={`flex-1 py-2.5 text-sm font-medium ${tab === "details" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500"}`}>Details</button>
          <button onClick={() => setTab("ledger")} className={`flex-1 py-2.5 text-sm font-medium ${tab === "ledger" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500"}`} data-testid="part-ledger-tab">Ledger ({txns.length})</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === "details" ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-slate-900">{part.quantity_on_hand}</div><div className="text-xs text-slate-500">On Hand</div></div>
                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-amber-600">{part.quantity_reserved}</div><div className="text-xs text-slate-500">Reserved</div></div>
                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold text-green-600">{part.quantity_available}</div><div className="text-xs text-slate-500">Available</div></div>
              </div>

              {/* Stock by location */}
              <div className="border border-slate-200 rounded-lg p-3" data-testid="stock-by-location">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> Stock by Location</span>
                  {canManage && (
                    <button onClick={() => setTransferOpen(true)} className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid="move-stock-btn">
                      <ArrowRightLeft className="w-3.5 h-3.5" /> Move
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {(part.stock_locations || []).map((b) => (
                    <div key={b.location_id || "none"} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 truncate">{b.location_label || "Unknown location"}</span>
                      <span className="font-medium text-slate-900 tabular-nums shrink-0">{b.quantity}</span>
                    </div>
                  ))}
                  {(part.unallocated || 0) > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-amber-600">Unassigned</span>
                      <span className="font-medium text-amber-600 tabular-nums">{part.unallocated}</span>
                    </div>
                  )}
                  {(part.stock_locations || []).length === 0 && (part.unallocated || 0) <= 0 && (
                    <div className="text-xs text-slate-400">No stock on hand</div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                <span className="text-sm text-slate-600">Inventory Value</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-slate-900">{formatCurrency(part.inventory_value)}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${vt.bg} ${vt.color}`}>{vt.label}</span>
                </div>
              </div>
              <div>
                <Row label="Unit Cost" value={formatCurrency(part.unit_cost)} />
                <Row label="Manufacturer" value={part.manufacturer} />
                <Row label="Brand" value={part.brand} />
                <Row label="OEM #" value={part.oem_part_number} />
                <Row label="Vendor #" value={part.vendor_part_number} />
                <Row label="Alternates" value={(part.alternate_part_numbers || []).join(", ")} />
                <Row label="Category" value={part.category} />
                <Row label="Subcategory" value={part.subcategory} />
                <Row label="Component Group" value={part.component_group} />
                <Row label="Compatibility" value={compat} />
                <Row label="Engine Compatibility" value={part.engine_compatibility} />
                <Row label="Location" value={part.location_label} />
                <Row label="Reorder Point" value={part.reorder_point} />
                <Row label="Value Source" value={part.value_source} />
                <Row label="Description" value={part.description} />
                <Row label="Notes" value={part.notes} />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {txns.length === 0 ? (
                <div className="text-center py-10 text-slate-400"><History className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No transactions yet</p></div>
              ) : txns.map((t) => {
                const cfg = TRANSACTION_LABELS[t.transaction_type] || { label: t.transaction_type, color: "text-slate-700", bg: "bg-slate-100" };
                return (
                  <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-100" data-testid="ledger-entry">
                    <div className="min-w-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                      <div className="text-xs text-slate-500 mt-1">{formatDateTime(t.date)} · {t.user}</div>
                      {t.reason && <div className="text-xs text-slate-500">{t.reason}{t.notes ? ` — ${t.notes}` : ""}</div>}
                      {t.unit_number && <div className="text-xs text-slate-500">Unit {t.unit_number}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-sm font-semibold ${t.quantity >= 0 ? "text-green-600" : "text-red-600"}`}>{t.quantity >= 0 ? "+" : ""}{t.quantity}</div>
                      <div className="text-xs text-slate-400">{t.previous_quantity} → {t.new_quantity}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap gap-2 px-5 py-4 border-t border-slate-200 flex-shrink-0">
            <Button variant="outline" className="flex-1 min-w-[88px]" onClick={() => setAdjustOpen(true)} data-testid="adjust-qty-btn"><Sliders className="w-4 h-4 mr-1" /> {t("Adjust")}</Button>
            <Button variant="outline" className="flex-1 min-w-[88px]" onClick={() => setIssueOpen(true)} data-testid="issue-part-btn"><Wrench className="w-4 h-4 mr-1" /> {t("Issue")}</Button>
            <Button className="flex-1 min-w-[88px]" onClick={() => onEdit(part)} data-testid="edit-part-btn"><Pencil className="w-4 h-4 mr-1" /> {t("Edit")}</Button>
            <Button variant="outline" className="px-3" onClick={() => setMergeOpen(true)} data-testid="merge-part-btn" aria-label="Merge"><GitMerge className="w-4 h-4" /></Button>
            <Button variant="outline" className="px-3 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmDelete(true)} data-testid="delete-part-btn" aria-label="Delete"><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}

        {/* Adjust dialog */}
        <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
          <DialogContent className="max-w-md" data-testid="adjust-dialog">
            <DialogTitle className="sr-only">Adjust Quantity</DialogTitle>
            <h3 className="text-lg font-semibold mb-3">Adjust Quantity</h3>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">New Quantity (current: {part.quantity_on_hand})</Label><Input type="number" value={newQty} onChange={(e) => setNewQty(e.target.value)} data-testid="adjust-qty-input" /></div>
              <div><Label className="text-xs text-slate-500">Reason</Label>
                <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {ADJUSTMENT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setAdjustOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={submitAdjust} data-testid="adjust-submit">Save Adjustment</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Issue to Unit dialog */}
        <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
          <DialogContent className="max-w-md" data-testid="issue-dialog">
            <DialogTitle className="sr-only">Issue Part to Unit</DialogTitle>
            <h3 className="text-lg font-semibold mb-3">Issue to Unit</h3>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Unit</Label>
                <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={issueUnit} onChange={(e) => setIssueUnit(e.target.value)} data-testid="issue-unit-select">
                  <option value="">Select unit...</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unit_number} ({v.vehicle_type?.replace("_", " ")})</option>)}
                </select>
              </div>
              <div><Label className="text-xs text-slate-500">Quantity (available: {part.quantity_available})</Label><Input type="number" min="1" value={issueQty} onChange={(e) => setIssueQty(e.target.value)} data-testid="issue-qty-input" /></div>
              <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={issueNotes} onChange={(e) => setIssueNotes(e.target.value)} /></div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setIssueOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={submitIssue} data-testid="issue-submit">Issue Part</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          title={`${t("Delete")} ${part.name}?`}
          message={t("This removes it from active lists. The record is kept in the audit trail.")}
        />
        <TransferStockDialog open={transferOpen} onClose={() => setTransferOpen(false)} part={part} locations={locations} currentUser={currentUser} onDone={onChanged} />
        <MergePartDialog open={mergeOpen} onClose={() => setMergeOpen(false)} part={part} currentUser={currentUser} onDone={() => { onChanged(); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
};
